import type {
  CapacitySummary,
  DirectoryInvitation,
  DirectoryMember,
  MemberDepartment,
  SuspensionReason,
} from "@closer/shared";
import {
  ACCOUNT_STATUS_NAMES,
  INVITATION_STATUS_NAMES,
  MEMBERSHIP_STATUS_NAMES,
  MEMBERSHIP_STATUSES,
} from "@closer/shared";
import { Badge, Card, FormField, Input, PageHeader, Select, Skeleton } from "@closer/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router";

import { ContextActivityModal } from "../../components/ContextActivityModal";
import { StateView } from "../../components/StateView";
import { api } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";
import { InvitationRowActions, InviteForm } from "./InvitationActions";
import { MemberActions } from "./MemberActions";

/**
 * Kullanıcılar ve Üyelikler (CLOS-189 `[051][S02]`).
 *
 * ### ÜÇ kayıt türü, ÜÇ yaşam döngüsü
 *
 * Kart: *"kullanıcı hesabı profili, organizasyon üyeliği ve davet üç ayrı kayıt türü ve
 * yaşam döngüsü olarak gösterilir"* ve *"kullanıcı hesabı kapatılması, bir organizasyon
 * üyeliğinin sona ermesiyle AYNI İŞLEM DEĞİLDİR."*
 *
 * Bu yüzden sayfa üç ekseni ayrı çiziyor: üyelik durumu satırın rozetinde, hesap durumu
 * yalnız BOZUKSA ikinci bir rozette, davetler ise kendi bölümünde. Tek bir "durum"
 * sütununda birleştirilseydi *"hesabı kapandı"* ile *"üyeliği sonlandırıldı"* ayırt
 * edilemez olurdu — yöneticinin atacağı adım ikisinde tamamen farklı.
 *
 * ### Yetki BÖLÜM BAŞINA
 *
 * Üyeler `organization.member`, davetler `organization.invite` istiyor ve uçlar da öyle
 * kapılı. Sayfa tek yetkiyle açılsaydı, üyeleri görebilen ama davet yönetemeyen bir
 * kullanıcı ya 403 duvarına toslar ya da göremeyeceği bir bölümü boş görürdü.
 *
 * ⚠ Görünürlük bir GÜVENLİK SINIRI DEĞİL: gerçek kapı her uçtaki `requirePermission`.
 * Buradaki hesap kullanıcıyı yapabileceği şeye götürüyor.
 *
 * ### Komutlar burada değil
 *
 * Askıya alma, sona erdirme, rol değiştirme ve davet yenileme/iptal `[051][S03]`'ün konusu.
 * Bu dosya OKUMA yüzeyi; kartın komutları zaten `[038]`/`[039]`/`[036]`'da ve sayfa
 * hiçbirinin kuralını yeniden yazmıyor.
 */

type UyeYaniti = {
  members: DirectoryMember[];
  suspensionReasons: SuspensionReason[];
  /** ATANABİLİR küme — yalnız aktif departmanlar (arşivliye atama sunucuda 409). */
  departments: MemberDepartment[];
};

/** Türkçe karşılaştırma: `toLowerCase()` "İLHAN"ı `i̇lhan` yapar ve arama kendi üyesini
 * bulamaz (`[065]`'te baş harflerde ölçülen tuzağın aynısı). */
function kucult(metin: string): string {
  return metin.toLocaleLowerCase("tr");
}

function uyelikRozetTonu(durum: DirectoryMember["status"]) {
  if (durum === "active") return "success" as const;
  if (durum === "suspended") return "warning" as const;
  return "neutral" as const;
}

export function MembersPage() {
  const { activeTenantId, can } = useAuth();
  const [arama, setArama] = useState("");
  const [durumFiltresi, setDurumFiltresi] = useState<string>("");

  /**
   * ⚠ Departman filtresi ADRESTEN başlıyor (CLOS-190: *"CLOS-189 sayfasına departman
   * filtresi ve güvenli bağlamla yönlendirilir"*). Yalnız state'te tutulsaydı Departmanlar
   * sayfasındaki bağlantı filtresiz bir listeye düşer ve yönetici aradığı kişileri kendisi
   * ayıklamak zorunda kalırdı.
   */
  const [aramaParam, setAramaParam] = useSearchParams();
  const departmanFiltresi = aramaParam.get("departman") ?? "";

  const tenantId = activeTenantId!;
  const davetGorulebilir = can("organization.invite");
  const uyeYonetebilir = can("organization.member");

  const uyeSorgusu = useQuery({
    queryKey: tenantKey(tenantId, "members"),
    queryFn: async () =>
      (await api<UyeYaniti>(`/api/tenants/${tenantId}/members`, { tenantId })).data,
  });

  const davetSorgusu = useQuery({
    queryKey: tenantKey(tenantId, "invites"),
    enabled: davetGorulebilir,
    queryFn: async () =>
      (await api<DirectoryInvitation[]>(`/api/tenants/${tenantId}/invites`, { tenantId })).data,
  });

  /**
   * Kapasite AYRI bir sorgu ve düşmesi sayfayı düşürmüyor: koltuk özeti bir bağlam
   * bilgisi, kullanıcı listesinin ön koşulu değil. Tek sorguda birleştirilseydi kapasite
   * ucundaki bir arıza yöneticiyi kendi kullanıcı listesinden de ederdi.
   */
  const kapasiteSorgusu = useQuery({
    queryKey: tenantKey(tenantId, "capacity"),
    queryFn: async () =>
      (await api<CapacitySummary>(`/api/tenants/${tenantId}/capacity`, { tenantId })).data,
  });

  if (uyeSorgusu.isPending) {
    return (
      <div role="status" aria-label="Kullanıcılar yükleniyor">
        <Skeleton lines={6} />
      </div>
    );
  }

  if (uyeSorgusu.error || !uyeSorgusu.data) {
    return (
      <StateView
        state={resolveErrorState(uyeSorgusu.error, {
          idempotent: true,
          existenceIsSecret: false,
          overrides: {
            error: {
              title: "Kullanıcılar yüklenemedi",
              description: "Liste getirilemedi. Lütfen tekrar deneyin.",
            },
          },
        })}
        onRetry={() => void uyeSorgusu.refetch()}
      />
    );
  }

  const arananMetin = kucult(arama.trim());
  const uyeler = uyeSorgusu.data.members.filter((uye) => {
    if (durumFiltresi && uye.status !== durumFiltresi) return false;
    if (departmanFiltresi && !uye.departments.some((d) => d.id === departmanFiltresi)) {
      return false;
    }

    if (!arananMetin) return true;
    return kucult(uye.name).includes(arananMetin) || kucult(uye.email).includes(arananMetin);
  });

  const kapasite = kapasiteSorgusu.data;
  const davetler = davetSorgusu.data ?? [];

  return (
    <div className="flex w-full flex-col gap-6">
      <PageHeader
        title="Kullanıcılar"
        description="Organizasyonunuzdaki kullanıcıları, üyelik durumlarını ve bekleyen davetleri yönetin."
      />

      {kapasite ? (
        <Card title="Koltuk kullanımı">
          <div className="flex flex-col gap-1" data-testid="kapasite-ozeti">
            <p className="text-sm text-fg">
              <span className="font-medium">
                {kapasite.used} / {kapasite.limit === null ? "sınırsız" : kapasite.limit}
              </span>{" "}
              koltuk kullanımda
            </p>
            {/*
              Rezervasyon üyeden AYRI sayılıyor: bekleyen bir davet koltuğu tutuyor ama
              henüz bir üye değil. Birleştirilseydi yönetici "12 kişi görüyorum ama 14
              koltuk dolu" farkını hiçbir yerde açıklayamazdı.
            */}
            <p className="text-sm text-fg-muted">
              {kapasite.members} üye · {kapasite.reservations} bekleyen davet
            </p>
            {/*
              ⚠ Engelin NEDENİ burada değil, davet formunun yanında: gerekçe engellediği
              eylemin dibinde durmalı. İki yerde birden yazsaydık aynı uyarı tek ekranda
              iki kez görünür, kullanıcı ikisinin farklı şeyler olduğunu sanırdı.
            */}
          </div>
        </Card>
      ) : null}

      <Card title="Kullanıcılar">
        <div className="flex flex-col gap-4">
          <div className="grid gap-3 sm:grid-cols-[2fr_1fr_1fr]">
            <FormField label="Ara">
              <Input
                value={arama}
                placeholder="Ad veya e-posta"
                onChange={(event) => setArama(event.target.value)}
              />
            </FormField>
            <FormField label="Üyelik durumu">
              <Select
                value={durumFiltresi}
                onChange={(event) => setDurumFiltresi(event.target.value)}
              >
                <option value="">Tümü</option>
                {MEMBERSHIP_STATUSES.map((durum) => (
                  <option key={durum} value={durum}>
                    {MEMBERSHIP_STATUS_NAMES[durum]}
                  </option>
                ))}
              </Select>
            </FormField>
            <FormField label="Departman">
              <Select
                value={departmanFiltresi}
                onChange={(event) => {
                  const secim = event.target.value;
                  // Adres GÜNCELLENİYOR: filtreli liste paylaşılabilir bir bağlantı olmalı.
                  setAramaParam(secim ? { departman: secim } : {}, { replace: true });
                }}
              >
                <option value="">Tümü</option>
                {uyeSorgusu.data.departments.map((departman) => (
                  <option key={departman.id} value={departman.id}>
                    {departman.name}
                  </option>
                ))}
              </Select>
            </FormField>
          </div>

          {uyeler.length === 0 ? (
            <p className="py-6 text-center text-sm text-fg-muted">
              {uyeSorgusu.data.members.length === 0
                ? "Bu organizasyonda henüz kullanıcı yok"
                : "Aramanıza uyan kullanıcı yok"}
            </p>
          ) : (
            <ul className="flex flex-col divide-y divide-border-default">
              {uyeler.map((uye) => (
                <li
                  key={uye.id}
                  data-testid="uye-satiri"
                  className="flex flex-wrap items-center justify-between gap-2 py-3"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-fg-strong">{uye.name}</p>
                    <p className="truncate text-sm text-fg-muted">{uye.email}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-fg-muted">{uye.roleName ?? "Rol atanmamış"}</span>
                    {uye.departments.map((departman) => (
                      <Badge key={departman.id}>{departman.name}</Badge>
                    ))}
                    <Badge tone={uyelikRozetTonu(uye.status)}>
                      {MEMBERSHIP_STATUS_NAMES[uye.status]}
                    </Badge>
                    {/*
                      ⚠ Hesap rozeti YALNIZ hesap bozuksa. Sağlam hesapta ikinci bir rozet
                      basmak her satırı "Aktif · Aktif" yapar ve gerçekten dikkat isteyen
                      satır gürültünün içinde kaybolurdu.

                      "Hesap:" öneki de gerekli: yanında üyelik rozeti dururken çıplak bir
                      "Kapatıldı", ÜYELİĞİN kapatıldığı anlamına gelirdi.
                    */}
                    {uye.accountStatus !== "active" ? (
                      <Badge tone="danger">Hesap: {ACCOUNT_STATUS_NAMES[uye.accountStatus]}</Badge>
                    ) : null}
                    {/*
                      ⚠ Menü GÖRÜNÜRLÜĞÜ bir güvenlik sınırı değil: gerçek kapı
                      `requirePermission("organization.member")`. Buradaki hesap yalnız
                      kullanıcıyı yapamayacağı bir işlemin üzerine göndermemek için.
                    */}
                    {/*
                      ⚠ ÜYELİK AKTİVİTESİNİN TÜKETİCİSİ (CLOS-881): `[055]` üyelik
                      satırlarını `membershipId` bağlamıyla üretiyor ve kapısı
                      (`organization.member`) yazılı, ama onları soran hiçbir yüzey yoktu.

                      ⚠ `uye.id` ÜYELİK kimliği, `uye.userId` DEĞİL — projektör bağlamı
                      `membershipId` ile kuruyor. Kullanıcı kimliğini geçseydik akış her
                      zaman BOŞ dönerdi ve arıza "bu üyenin geçmişi yok" gibi görünürdü.
                    */}
                    <ContextActivityModal
                      entityType="membership"
                      entityId={uye.id}
                      baslik={uye.name}
                    />
                    {uyeYonetebilir ? (
                      <MemberActions
                        tenantId={tenantId}
                        member={uye}
                        suspensionReasons={uyeSorgusu.data.suspensionReasons}
                        departments={uyeSorgusu.data.departments}
                      />
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Card>

      {davetGorulebilir ? (
        <Card title="Davetler">
          <div className="flex flex-col gap-4">
            <InviteForm tenantId={tenantId} capacity={kapasite} />

            {davetler.length === 0 ? (
              <p className="py-6 text-center text-sm text-fg-muted">Bekleyen davet yok</p>
            ) : (
              <ul className="flex flex-col divide-y divide-border-default">
                {davetler.map((davet) => (
                  <li
                    key={davet.id}
                    data-testid="davet-satiri"
                    className="flex flex-wrap items-center justify-between gap-2 py-3"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-fg-strong">{davet.email}</p>
                      <p className="truncate text-sm text-fg-muted">{davet.roleName}</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge tone={davet.status === "pending" ? "neutral" : "warning"}>
                        {INVITATION_STATUS_NAMES[davet.status]}
                      </Badge>
                      <InvitationRowActions tenantId={tenantId} invitation={davet} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>
      ) : null}
    </div>
  );
}
